/**
 * SimulateFeed — лента «Проверить бота» как в WhatsApp: новые снизу, своё
 * сообщение видно сразу, пока бот думает, а лента сама едет к последнему.
 */

import {useEffect, useRef} from 'react';

import type {SimulatedMessage} from '@services/assistant';
import {cn} from '@lib/utils';

/** Пузырь переписки: пациент слева, бот справа — как в чате кабинета */
const Bubble = ({m, pending = false}: {m: SimulatedMessage; pending?: boolean}) => {
  const patient = m.role === 'patient';
  return (
    <div
      className={cn(
        'max-w-[85%] whitespace-pre-wrap break-words rounded-[12px] px-3 py-2 text-[13px] leading-[1.45]',
        patient ? 'self-start bg-card' : 'self-end bg-primary/10',
        pending && 'opacity-60',
      )}
    >
      {m.body}
      {!patient && !m.sent && (
        <div className="mt-1 text-[10.5px] font-semibold text-skr-faint">не ушло пациенту</div>
      )}
    </div>
  );
};

type Props = {
  /** Хвост диалога с сервера, новые снизу */
  thread: SimulatedMessage[];
  /** Своё сообщение в пути; `null` — ничего не отправляется */
  outgoing: string | null;
  /** Итог последней отправки: «Ответ ушёл пациенту», «Передано человеку»… */
  note: string | null;
};

export const SimulateFeed = ({thread, outgoing, note}: Props) => {
  const feed = useRef<HTMLDivElement>(null);

  // Новое внизу — лента сама едет к нему
  useEffect(() => {
    const el = feed.current;
    el?.scrollTo({top: el.scrollHeight, behavior: 'smooth'});
  }, [thread, outgoing]);

  return (
    <div
      ref={feed}
      className="flex h-[320px] flex-col gap-2 overflow-y-auto rounded-[11px] bg-[#F7F8FA] p-3"
    >
      {thread.length === 0 && outgoing === null && (
        <div className="m-auto max-w-[240px] text-center text-[12.5px] leading-[1.45] text-skr-faint">
          Напишите как пациент — ответ бота появится здесь
        </div>
      )}
      {thread.map((m, i) => (
        <Bubble key={i} m={m} />
      ))}
      {outgoing !== null && (
        <>
          <Bubble m={{role: 'patient', body: outgoing, sent: true}} pending />
          <div className="animate-pulse self-end rounded-[12px] bg-primary/10 px-3 py-2 text-[13px] text-skr-ink2">
            Бот думает…
          </div>
        </>
      )}
      {note !== null && (
        <div className="mt-1 self-center text-center text-[11.5px] font-semibold text-skr-ink2">
          {note}
        </div>
      )}
    </div>
  );
};
